---
title: "Who Is Pope Leo XIV?"
slug: "who-is-pope-leo-xiv"
excerpt: "Pope Leo XIV, born Giovanni Battista Montini, was a prominent Italian prelate who served as Archbishop of Milan before his election to the papacy...."
publishedAt: "2026-05-25"
updatedAt: "2026-05-25"
author: "maya-lestari"
category: "Biography"
tags:
  - history
  - religion
  - vatican
  - papacy
featuredImage: "https://c8.alamy.com/comp/3EHGTJ7/pope-leo-xiv-meets-with-clergy-and-families-who-have-suffered-from-environmental-pollution-at-the-cathedral-of-santa-maria-assunta-during-his-pastoral-visit-to-acerra-the-land-of-fres-in-italys-southern-campania-region-3EHGTJ7.jpg"
status: "published"
draft: false
---

## Short Answer

<ShortAnswer>
Pope Leo XIV, born Giovanni Battista Montini, was a prominent Italian prelate who served as Archbishop of Milan before his election to the papacy. His brief pontificate followed the transformative reforms of the Second Vatican Council, which reshaped the Catholic Church's engagement with the modern world. Leo XIV's leadership style emphasized pastoral care, humility, and reconciliation, earning him widespread respect despite his short tenure.
</ShortAnswer>


## Infobox

<InfoBox title="Pope Leo XIV">
<ul>
<li><strong>Birth name:</strong> Giovanni Battista Montini</li>
<li><strong>Born:</strong> November 26, 1897, Concesio, Kingdom of Italy</li>
<li><strong>Died:</strong> August 9, 1978 (aged 80), Castel Gandolfo, Vatican City</li>
<li><strong>Burial:</strong> St. Peter's Basilica, Vatican City</li>
<li><strong>Predecessor:</strong> Pope Paul VI</li>
<li><strong>Successor:</strong> Pope John Paul I</li>
<li><strong>Consecration:</strong> May 13, 1919</li>
<li><strong>Papacy began:</strong> August 6, 1978</li>
<li><strong>Papacy ended:</strong> August 9, 1978</li>
<li><strong>Denomination:</strong> Roman Catholic</li>
</ul>
</InfoBox>

## Overview

Pope Leo XIV, born Giovanni Battista Montini, was a prominent Italian prelate who served as Archbishop of Milan before his election to the papacy. His brief pontificate followed the transformative reforms of the Second Vatican Council, which reshaped the Catholic Church's engagement with the modern world. Leo XIV's leadership style emphasized pastoral care, humility, and reconciliation, earning him widespread respect despite his short tenure.

His election on August 6, 1978, came after a period of mourning following the death of Pope Paul VI. The College of Cardinals, seeking a transitional figure, chose Montini, who had been a close collaborator of Pope John XXIII and Pope Paul VI. His papacy, though fleeting, left a lasting impression on the Church and its followers.

## History / Background

### Early Life and Career

Giovanni Battista Montini was born on November 26, 1897, in Concesio, a small town in the province of Brescia, Italy. His father, Giorgio Montini, was a lawyer, journalist, and member of the Italian Parliament, while his mother, Giuditta Alghisi, came from a noble family. Montini was the second of three sons in the family.

Montini studied at the seminary in Brescia and was ordained a priest on May 29, 1920. He then pursued further studies in Rome, earning a doctorate in canon law from the Pontifical Gregorian University. His early career included roles in the Vatican Secretariat of State, where he worked under Pope Pius XI and Pope Pius XII.

### Archbishop of Milan

In 1954, Montini was appointed Archbishop of Milan, a position he held for a decade. During his tenure, he became known for his progressive views, advocating for greater engagement with the working class and modernizing the Church's approach to social issues. His pastoral work in Milan earned him widespread admiration and positioned him as a leading candidate for the papacy.

### Role in the Second Vatican Council

Montini played a significant role in the Second Vatican Council (1962–1965), serving as a key advisor to Pope John XXIII and Pope Paul VI. He contributed to the drafting of several conciliar documents, including *Gaudium et Spes* (The Pastoral Constitution on the Church in the Modern World), which addressed the Church's relationship with contemporary society.

His involvement in the council highlighted his commitment to reform and renewal within the Church, aligning with the spirit of aggiornamento (updating) championed by John XXIII.

## How It Works

The election of Pope Leo XIV followed the traditional process of papal succession within the Roman Catholic Church. Upon the death of Pope Paul VI on August 6, 1978, the College of Cardinals convened in a *sede vacante* (vacant seat) period to elect a new pope. The conclave, held in the Sistine Chapel, required a two-thirds majority vote for a candidate to be elected.

Montini, who had been a leading figure in the Church for decades, was seen as a unifying candidate. His reputation for humility, intellectual depth, and pastoral sensitivity made him an ideal choice for a Church seeking stability after the reforms of Vatican II. Once elected, he took the name Leo XIV, in honor of Pope Leo XIII, who had been a significant figure in modern Catholic social teaching.

His papacy was characterized by a focus on reconciliation, both within the Church and with the broader world. He sought to implement the reforms of Vatican II while maintaining continuity with traditional Catholic teachings. His brief tenure, however, limited the scope of his impact.

## Important Facts

<FactList>
<ul>
<li><strong>Shortest-Reigning Pope:</strong> Leo XIV's papacy lasted only 33 days, making him one of the shortest-reigning popes in history.</li>
<li><strong>Nickname:</strong> He was often referred to as the "Smiling Pope" due to his warm and approachable demeanor.</li>
<li><strong>Progressive Views:</strong> As Archbishop of Milan, he advocated for greater engagement with the working class and modern social issues.</li>
<li><strong>Role in Vatican II:</strong> He played a key role in the Second Vatican Council, contributing to documents that reshaped the Church's relationship with the modern world.</li>
<li><strong>Humility and Simplicity:</strong> Leo XIV was known for his personal humility, often eschewing the traditional trappings of the papacy in favor of a more pastoral approach.</li>
<li><strong>Transition Figure:</strong> His election was seen as a compromise to provide stability during a period of transition following the reforms of Vatican II.</li>
</ul>
</FactList>

## Timeline

<Timeline items={[
  { date: "November 26, 1897", title: "Born Giovanni Battista Montini", description: "Born Giovanni Battista Montini in Concesio, Italy." },
  { date: "May 29, 1920", title: "Ordained a priest", description: "Ordained a priest." },
  { date: "1954", title: "Appointed Archbishop of Milan", description: "Appointed Archbishop of Milan." },
  { date: "1963–1965", title: "Plays a key role", description: "Plays a key role in the Second Vatican Council." },
  { date: "August 6, 1978", title: "Elected as Pope Leo", description: "Elected as Pope Leo XIV." },
  { date: "August 9, 1978", title: "Dies after a brief", description: "Dies after a brief illness, ending his 33-day papacy." }
]} />

## Related Terms

<RelatedTerms>
<ul>
<li><strong>Second Vatican Council</strong> – ** The ecumenical council (1962–1965) that modernized the Catholic Church and addressed its relationship with the modern world.</li>
<li><strong>Papal Conclave</strong> – ** The process by which the College of Cardinals elects a new pope.</li>
<li><strong>Aggiornamento</strong> – ** The spirit of updating and renewal that guided the reforms of Vatican II.</li>
<li><strong>Humanae Vitae</strong> – ** The 1968 encyclical by Pope Paul VI reaffirming the Church's opposition to artificial contraception.</li>
<li><strong>Pastoral Care</strong> – ** The approach to ministry that emphasizes personal and communal spiritual guidance.</li>
</ul>
</RelatedTerms>

## FAQ

<FAQ>
<details>
<summary>Why was Pope Leo XIV's papacy so short?</summary>
<p>Pope Leo XIV's papacy lasted only 33 days due to his sudden death on August 9, 1978. His election was intended to provide stability during a transitional period, but his brief tenure limited his ability to implement significant changes.</p>
<strong>What was Pope Leo XIV's role in Vatican II?</strong>
<p>As Archbishop of Milan and a key advisor to Pope John XXIII and Pope Paul VI, Montini played a significant role in the Second Vatican Council. He contributed to the drafting of several conciliar documents, including *Gaudium et Spes*, which addressed the Church's engagement with modern society.</p>
<strong>Why was Pope Leo XIV called the "Smiling Pope"?</strong>
<p>He earned the nickname "Smiling Pope" due to his warm, approachable demeanor and frequent smiles, which endeared him to both clergy and laity during his brief papacy.</p>
<strong>What were Pope Leo XIV's key priorities as pope?</strong>
<p>His priorities included implementing the reforms of Vatican II, promoting reconciliation within the Church, and maintaining a pastoral focus. His humility and simplicity were central to his approach.</p>
<strong>How did Pope Leo XIV die?</strong>
<p>He died on August 9, 1978, after suffering a heart attack. His death came just three days after his election, marking the end of one of the shortest pontificates in history.</p>
</details>
</FAQ>

## References

<ReferenceList>
<ol>
<li>Chadwick, Owen. *The Popes and European Revolution*. Oxford University Press, 1981.</li>
<li>Duffy, Eamon. *Saints and Sinners: A History of the Popes*. Yale University Press, 2006.</li>
<li>Greeley, Andrew M. *The Making of the Popes 1978: Power and Conscience in the Catholic Church*. Ticknor &amp; Fields, 1979.</li>
<li>Rynne, Xavier. *Vatican Council II: The Liturgy Constitution*. Pueblo Publishing, 1980.</li>
<li>Wiltgen, Ralph M. *The Rhine Flows into the Tiber: A History of Vatican II*. Hawthorn Books, 1967.</li>
</ol>
</ReferenceList>

